// Bounds the LaunchDarkly bootstrap cache's localStorage footprint (ENG-1813).
//
// `bootstrap: "localStorage"` (src/index.tsx, ENG-1729) makes the SDK cache the
// whole flag set under a key derived from the *entire serialized context*:
//
//   PersistentFlagStore.getFlagsKey() ->
//     `ld:${environment}:${hash || btoa(JSON.stringify(context))}`
//
// `hash` is the secure-mode HMAC and is not set here, so any change to any
// context attribute produces a brand-new key — and the SDK's `clearFlags()`
// only ever clears the key it is currently using, never the ones it has
// superseded. With ~465 flags cached with per-flag metadata, each orphan is on
// the order of 100KB, so a browser profile that churns its context fills the
// origin's ~5MB budget in roughly 50 page loads. Once full, every other
// localStorage writer in the app starts failing, which is how this surfaced:
// as a flood of QuotaExceededError from the unrelated `noah-sync-*` namespace,
// the one writer that logs rather than swallowing (ENG-1796).
//
// `buildStableLdContext` (hooks/use-authorize.hook.tsx) removes the known
// churn source, so a healthy profile settles at two keys: the anonymous
// bootstrap context and the identified user. This module is the safety net for
// the general case — a context attribute nobody predicted starts varying, or a
// browser already carrying orphans from before that fix shipped.

const LD_KEY_PREFIX = "ld:";

// A healthy profile holds two entries (anonymous bootstrap + identified user).
// The allowance above that covers shared workstations where several staff sign
// in to the same browser profile without clearing it.
const MAX_EXPECTED_LD_CACHE_KEYS = 8;

function getLaunchDarklyCacheKeys(): string[] {
  try {
    return Object.keys(localStorage).filter((key) =>
      key.startsWith(LD_KEY_PREFIX)
    );
  } catch {
    return [];
  }
}

/**
 * Drops the LaunchDarkly bootstrap cache when it has accumulated more entries
 * than a healthy profile can account for. Safe to call on every boot: a
 * missing cache is a cache miss, and the SDK falls back to its normal network
 * fetch — flag *values* are never served stale or empty by this, only the
 * render-immediately-from-cache optimisation is skipped on the one load that
 * prunes.
 *
 * Deletes all of them rather than trying to keep the live one. The live key is
 * only knowable by replicating the SDK's internal derivation, and guessing
 * wrong would evict the entry actually in use while leaving the orphans.
 * Orphans are never read, so the only cost of clearing the set is that single
 * cache miss.
 *
 * Never throws.
 */
export function pruneOrphanedLaunchDarklyCache(): number {
  const keys = getLaunchDarklyCacheKeys();
  if (keys.length <= MAX_EXPECTED_LD_CACHE_KEYS) return 0;

  let removed = 0;
  keys.forEach((key) => {
    try {
      localStorage.removeItem(key);
      removed += 1;
    } catch {
      // Storage unavailable mid-loop; leave the rest for the next boot.
    }
  });

  return removed;
}
